"use client";

/**
 * OptionSheet — 목록에서 하나를 고르는 시트.
 * @description 앱 `shared/ui/OptionSheet` 을 옮겼다. `Select` 가 여는 자리다. 시안에 대응물이 없고 DS 조각
 *   (`BottomSheet`·`Search`·`ListSelection`)을 조립한 구조물이라는 점도 같다.
 *
 *   **검색이 있는 시트만** 높이를 고정한다. 검색은 목록을 줄였다 늘렸다 하고, 내용 높이에
 *   맞추는 시트는 그때마다 따라 움직인다 — "China" 를 치는 동안 시트가 발밑에서 무너지는
 *   셈이다. 검색이 없으면 목록이 처음부터 끝까지 그대로라 고정할 이유가 없다.
 */
import { BottomSheet, ListSelection, Search } from "@fanwork-web/design-system";
import { useMemo, useState } from "react";

import type { OptionSheetProps } from "./OptionSheet.types";

const SEARCHABLE_HEIGHT = "90%";

export function OptionSheet({
  visible,
  onClose,
  title,
  options,
  selected,
  onSelect,
  searchPlaceholder,
  clearLabel = "Clear",
  emptyLabel,
}: OptionSheetProps) {
  const [query, setQuery] = useState("");

  const visibleOptions = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(keyword),
    );
  }, [options, query]);

  function handleSelect(value: string) {
    onSelect(value);
    setQuery("");
    onClose();
  }

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      label={title}
      heightRatio={searchPlaceholder ? SEARCHABLE_HEIGHT : undefined}
    >
      <div className="flex flex-col gap-[var(--fw-space-gap-md)]">
        <h2 className="fw-h3 text-[var(--fw-color-content-neutral-primary)]">
          {title}
        </h2>

        {searchPlaceholder ? (
          <Search
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
            clearLabel={clearLabel}
          />
        ) : null}

        {visibleOptions.length === 0 ? (
          <p className="fw-l1 py-[var(--fw-space-inset-lg)] text-center text-[var(--fw-color-content-neutral-tertiary)]">
            {emptyLabel}
          </p>
        ) : (
          <div role="listbox" aria-label={title} className="flex flex-col">
            {visibleOptions.map((option) => (
              <ListSelection
                key={option.value}
                title={option.label}
                selected={option.value === selected}
                onSelect={() => handleSelect(option.value)}
              />
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
